import { FC, useMemo, useState, useCallback, useEffect } from 'react'
import {
  Box,
  ButtonBase,
  Typography,
  LinearProgress,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Badge,
  IconButton,
  Tooltip,
  useMediaQuery,
  ListSubheader
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ArrowForward,
  CheckCircleOutline,
  Close,
  CloudUploadOutlined,
  HighlightOff,
  Send
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import OperationsFrozenMessage from 'components/OperationsFrozenMessage'
import Icon from 'components/Icon'
import { getApiErrorMessage } from 'utils'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useUserFlats } from 'services/contracts/hooks/useUserFlats'
import { useUserRoles, isOwnerOnly, isOwner } from 'services/rbac/useUserRoles'
import { useIntl } from 'react-intl'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import MobileBottomFooter from 'components/MobileBottomFooter'
import { getFaultTint } from 'components/Helpdesk/helpdesk.utils'
import {
  FieldLabel,
  primaryButtonSx,
  submitButtonSx,
  optionChipSx,
  calendarCardSx,
  calendarSx
} from 'components/Helpdesk/FormPrimitives'
import {
  useHelpdeskCategoriesList,
  useHelpdeskTcketsCreate,
  useHelpdeskTicketsList,
  useHelpdeskTicketsMessagesCreate
} from 'services/helpdesk/hooks'
import { useLeaseContractFlats } from 'services/contracts/hooks/useLeaseContractsFlats'
import { DateCalendar } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import weekday from 'dayjs/plugin/weekday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Holidays from 'date-holidays'
import { useHelpdeskTicketsAvailability } from 'services/helpdesk/hooks/useHelpdeskTicketsAvailability'
import theme from 'styles/theme'
import { FAULT_TOPICS, topicsWithoutAvailabilityDate } from './defects.consts'
import { useLanguage } from 'services/i18n/i18n.hooks'
import { useDropzone } from 'react-dropzone'
import { CONTRACT_STATUS } from 'services/contracts/contracts.consts'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(weekday)
dayjs.extend(customParseFormat)
dayjs.extend(localizedFormat)

const disableDate = (date: dayjs.Dayjs) => {
  const day = new Date(dayjs(date).format()).getDay()
  const isWeekend = day === 0 || day === 6
  const isToday = dayjs().isSame(date, 'day')

  // Initialize the Polish holidays list
  const hd = new Holidays('PL')
  const holidays = hd.getHolidays(dayjs().year()).map((holiday) => holiday.date)

  // Check if the date is a holiday
  const isHoliday = holidays.some((holiday) => dayjs(holiday).isSame(date, 'day'))

  return isWeekend || isToday || isHoliday
}

const hd = new Holidays('PL')
const holidays = hd.getHolidays(dayjs().year()).map((holiday) => holiday.date)

const calculateMaxDate = (daysToAdd: number) => {
  let date = dayjs()
  let addedDays = 0

  while (addedDays < daysToAdd) {
    date = date.add(1, 'day')
    if (date.day() !== 0 && date.day() !== 6 && !holidays.includes(date.format('YYYY-MM-DD'))) {
      addedDays++
    }
  }

  return date
}

// 30-minute time slots between 9:00 and 17:00 on the given day.
const generateTimeSlots = (date?: dayjs.Dayjs) => {
  if (!date) {
    return []
  }
  const start = dayjs(date).startOf('day')
  const slots: dayjs.Dayjs[] = []
  for (let minutes = 9 * 60; minutes <= 17 * 60; minutes += 30) {
    slots.push(start.add(minutes, 'minute'))
  }
  return slots
}


const DefectsReportFault: FC = () => {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [parentCategoryId, setParentCategoryId] = useState<number>(0)
  const [contractId, setContractId] = useState<number>(0)
  // Index of the availability term (0..2) currently edited in the date step.
  const [editingIndex, setEditingIndex] = useState(0)
  const { createTicket } = useHelpdeskTcketsCreate()
  const { refetchTicketsList } = useHelpdeskTicketsList()
  const { createMessage } = useHelpdeskTicketsMessagesCreate()
  const { categoriesList } = useHelpdeskCategoriesList()
  const { leaseContractsList } = useLeaseContractsList()
  const { userFlats } = useUserFlats()
  const userRoles = useUserRoles()
  const isOnlyOwner = isOwnerOnly(userRoles)
  const isOwnerUser = isOwner(userRoles)

  const { leaseContractsFlats } = useLeaseContractFlats(contractId)
  const { createAvailability } = useHelpdeskTicketsAvailability()
  const { language } = useLanguage()

  const handleHoursRestriction = (timeValue: number, type: 'from' | 'to', index: number) => {
    const hour = dayjs(timeValue).hour()
    const now = dayjs()
    const isAfter5PM = now.hour() >= 17

    const selectedDate = formik.values.availableDates[index]?.availableDate
    const selectedHourFrom = formik.values.availableDates[index]?.availableHourFrom
    if (!selectedDate) return false

    const isNextDay = dayjs(selectedDate).isSame(dayjs().add(1, 'day'), 'day')
    const isBefore =
      !!selectedHourFrom && type === 'to'
        ? dayjs(timeValue).isSameOrBefore(selectedHourFrom)
        : false

    if (isNextDay && isAfter5PM && (hour < 10 || hour > 16)) {
      return true
    }
    if (hour < 9 || hour > 16) {
      return true
    }

    return isBefore
  }

  const formik = useFormik<{
    categories: number[]
    description: string
    topics: FAULT_TOPICS[]
    topic: string
    progress: number
    step: number
    flatId: number
    attachments: File[]
    availableDates: { availableDate: string; availableHourFrom: number; availableHourTo: number }[]
  }>({
    initialValues: {
      topics: [],
      topic: '',
      categories: [],
      description: '',
      progress: 0,
      step: 0,
      flatId: 0,
      attachments: [],
      availableDates: []
    },
    onSubmit: async ({ attachments, ...values }, { setSubmitting }) => {
      setSubmitting(true)
      let data: any
      try {
        ;({ data } = await createTicket({
          faultCategory: values.categories.at(-1) as number,
          topic: values.topic,
          description: values.description,
          flatId: values.flatId
        }))
      } catch (error: any) {
        const message = getApiErrorMessage(error)
        enqueueSnackbar(
          message ||
            formatMessage({
              id: 'defectsReportFault.error',
              defaultMessage: 'Nie można zgłosić usterki.'
            }),
          { variant: 'error' }
        )
        setSubmitting(false)
        return
      }

      if (!!attachments.length) {
        for (const attachment of attachments) {
          await createMessage({
            message: '',
            attachment,
            ticketId: data.id
          })
        }
      }

      if (values?.availableDates?.length) {
        values?.availableDates
          .filter((date) => date?.availableDate)
          .forEach((date) => {
            const hourFrom = date?.availableHourFrom
            const dayjsHourFrom = dayjs(hourFrom)
            const availableFrom = dayjs(
              new Date(dayjs(date?.availableDate).format()).setHours(
                hourFrom ? dayjsHourFrom.hour() : 0,
                hourFrom ? dayjsHourFrom.minute() : 0,
                0
              )
            ).format()
            let hourTo: any = date?.availableHourTo
            if (!hourTo) {
              hourTo = dayjs(date.availableDate).set('hour', 17).set('minute', 0).set('second', 0)
            } else {
              const dayjsHourTo = dayjs(hourTo)
              hourTo = dayjs(date?.availableDate)
                .set('hour', dayjsHourTo.hour())
                .set('minute', dayjsHourTo.minute())
                .set('second', 0)
            }

            const availableTo = hourTo.format()

            createAvailability({
              ticketId: data.id,
              availableFrom,
              availableTo
            })
          })
      }

      formik.setFieldValue('step', 3)
      refetchTicketsList()
      setSubmitting(false)
    }
  })

  const navigate = useNavigate()

  const handleNextStep = () => {
    if (formik.values.categories.includes(parentCategoryId)) {
      formik.setFieldValue(
        'categories',
        formik.values.categories.filter((category) => category !== parentCategoryId)
      )
    } else {
      formik.setFieldValue('categories', [...formik.values.categories, parentCategoryId])
    }
    formik.setFieldValue('progress', 33)
    return setParentCategoryId(0)
  }

  const handlePreviousStep = () => {
    if (!formik.values.categories.length) {
      navigate('/faults')
    }
    const decrementProgress = () => {
      if (formik.values.progress > 0) {
        formik.setFieldValue('progress', formik.values.progress - 33)
      }
    }

    const removeLastCategoryAndTopic = () => {
      const updatedCategories = [...formik.values.categories]
      if (updatedCategories.length > 0) {
        updatedCategories.pop()
        formik.setFieldValue('categories', updatedCategories)

        const newParentCategoryId = updatedCategories.at(-1) || 0
        setParentCategoryId(newParentCategoryId)
      }

      const updatedTopics = [...formik.values.topics]
      if (updatedTopics.length > 0) {
        updatedTopics.pop()
        formik.setFieldValue('topics', updatedTopics)
      }
    }

    if (formik.values.step === 0) {
      decrementProgress()
      removeLastCategoryAndTopic()
    } else if (formik.values.step > 0) {
      formik.setFieldValue('step', formik.values.step - 1)

      if (formik.values.step === 1) {
        removeLastCategoryAndTopic()
      }

      if (formik.values.step === 1 || formik.values.step === 2) {
        decrementProgress()
      }
    }
  }

  const getTitle = useCallback(() => {
    if (!!formik.values.categories.length) {
      const currentParentCategoryId = formik.values.categories.at(-1)
      return categoriesList.find(({ id }) => id === currentParentCategoryId)?.name || ''
    }

    return ''
  }, [categoriesList, formik])

  const Dropzone = ({ onDrop }: { onDrop: (files: File[]) => void }) => {
    const { getRootProps, getInputProps } = useDropzone({
      accept: 'image/*' as any,
      onDrop,
      multiple: true,
      maxFiles: 5
    })

    return (
      <Box
        {...getRootProps()}
        sx={{
          border: `1px dashed ${theme.r4r.greyLight}`,
          backgroundColor: '#fff',
          padding: '28px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px'
        }}>
        <input {...getInputProps()} data-cy='input-photo' />
        <CloudUploadOutlined sx={{ color: theme.r4r.grey, fontSize: '32px' }} />
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.r4r.navy }}>
          {formatMessage({
            id: 'helpdesk.dropFilesHere',
            defaultMessage: 'Przeciągnij i upuść pliki tutaj'
          })}
        </Typography>
        <Typography sx={{ fontSize: '12px', color: theme.r4r.grey }}>
          {formatMessage({
            id: 'helpdesk.orClickToSelect',
            defaultMessage: 'lub kliknij aby wybrać'
          })}
        </Typography>
      </Box>
    )
  }

  const handleDrop = (acceptedFiles: File[]) => {
    const currentAttachments = formik.values.attachments
    const filesToAdd = acceptedFiles.slice(0, 5 - currentAttachments.length) // Limit the files to 5
    formik.setFieldValue('attachments', [...currentAttachments, ...filesToAdd]) // Add new files
  }

  // Time slot chips (Od/Do godziny) for the currently edited availability term.
  const renderTimeChips = (type: 'from' | 'to') => {
    const term = formik.values.availableDates[editingIndex]
    const slots = generateTimeSlots(term?.availableDate ? dayjs(term.availableDate) : undefined)
    const selected = type === 'from' ? term?.availableHourFrom : term?.availableHourTo

    if (!slots.length) {
      return (
        <Typography sx={{ fontSize: '12px', color: theme.r4r.grey }}>
          {formatMessage({
            id: 'helpdesk.selectDateFirst',
            defaultMessage: 'Najpierw wybierz datę w kalendarzu'
          })}
        </Typography>
      )
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'repeat(8, 1fr)' : 'repeat(4, 1fr)',
          gap: '8px'
        }}>
        {slots.map((slot) => {
          const isDisabled = handleHoursRestriction(slot as any, type, editingIndex)
          const isSelected =
            !!selected && dayjs(selected).format('HH:mm') === slot.format('HH:mm')
          return (
            <ButtonBase
              key={slot.format('HH:mm')}
              disabled={isDisabled}
              onClick={() =>
                formik.setFieldValue(
                  `availableDates[${editingIndex}].availableHour${
                    type === 'from' ? 'From' : 'To'
                  }`,
                  slot
                )
              }
              sx={optionChipSx(isSelected, isDisabled)}>
              {slot.format('HH:mm')}
            </ButtonBase>
          )
        })}
      </Box>
    )
  }

  const title = useMemo(() => getTitle(), [getTitle])

  // Find billing/settlements category by name (more reliable than hardcoded ID)
  const billingCategory = useMemo(() => {
    return categoriesList.find(
      (cat) =>
        !cat.parentCategoryId &&
        (cat.name === 'Settlements' || cat.nameLocal === 'Rozliczenia')
    )
  }, [categoriesList])

  const billingCategoryId = billingCategory?.id

  // Find client portal category by name
  const clientPortalCategory = useMemo(() => {
    return categoriesList.find(
      (cat) =>
        !cat.parentCategoryId &&
        (cat.name === 'Client Portal' || cat.nameLocal === 'Portal klienta')
    )
  }, [categoriesList])

  const clientPortalCategoryId = clientPortalCategory?.id

  // Control the visibility of inactive contracts list for specific category ("Rozliczenia")
  const withInactiveContracts = billingCategoryId
    ? formik.values.categories.includes(billingCategoryId)
    : false

  // Filter ended contracts (users can report billing issues at any time after contract ends)
  const endedContracts = useMemo(() => {
    return leaseContractsList.filter(
      ({ leaseStatus }) => leaseStatus === CONTRACT_STATUS.contractStatusEnded
    )
  }, [leaseContractsList])

  // Check if user has only ended contracts (no active contracts)
  const hasOnlyEndedContracts = useMemo(() => {
    const hasActiveContract = leaseContractsList.some(({ leaseStatus }) =>
      [
        CONTRACT_STATUS.contractStatusActivationReady,
        CONTRACT_STATUS.contractStatusTypeActive,
        CONTRACT_STATUS.contractStatusDepositPaid
      ].includes(leaseStatus)
    )
    return !hasActiveContract && endedContracts.length > 0
  }, [leaseContractsList, endedContracts])

  // Determine if the selected surface (tenant contract or owner/tenant flat) is operations-frozen.
  const isSelectedSurfaceFrozen = useMemo(() => {
    const contractFrozen = leaseContractsList.find((c) => c.id === contractId)?.isOperationsFrozen
    const flatFrozen = (userFlats || []).find((flat) => flat.id === formik.values.flatId)?.investment
      ?.operationsFrozen

    return Boolean(contractFrozen || flatFrozen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseContractsList, userFlats, contractId, formik.values.flatId])

  const categories = useMemo(() => {
    // If navigating within subcategories, show children of current parent
    if (!!formik.values.categories.length) {
      const currentParentCategoryId = formik.values.categories.at(-1)
      return categoriesList.filter(
        ({ parentCategoryId }) => parentCategoryId === currentParentCategoryId
      )
    }

    // At root level: get all root categories (those without parent)
    let rootCategories = categoriesList.filter(({ parentCategoryId }) => !parentCategoryId)

    // Hide Settlements/Billing and Client Portal categories for owners
    if (isOnlyOwner) {
      rootCategories = rootCategories.filter(({ id }) => {
        // Filter out Settlements category
        if (billingCategoryId && id === billingCategoryId) return false
        // Filter out Client Portal category
        if (clientPortalCategoryId && id === clientPortalCategoryId) return false
        return true
      })
    }

    // If user has only ended contracts (no active), show only billing category
    if (hasOnlyEndedContracts && !isOnlyOwner && billingCategoryId) {
      return rootCategories.filter(({ id }) => id === billingCategoryId)
    }

    // Otherwise show all root categories
    return rootCategories
  }, [
    categoriesList,
    formik.values.categories,
    hasOnlyEndedContracts,
    isOnlyOwner,
    billingCategoryId,
    clientPortalCategoryId
  ])

  // Reset contract selection when switching between categories (e.g., from Billing to Technical Issues)
  // to prevent selecting an ended contract in categories that don't allow it
  useEffect(() => {
    if (contractId !== 0) {
      const selectedContract = leaseContractsList.find((c) => c.id === contractId)

      if (!selectedContract) return

      // Check if current contract is ended
      const isContractEnded = selectedContract.leaseStatus === CONTRACT_STATUS.contractStatusEnded

      // If not in Billing category and contract is ended, reset selection
      if (!withInactiveContracts && isContractEnded) {
        setContractId(0)
        formik.setFieldValue('flatId', 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withInactiveContracts])

  // Automatically select first available contract (active or ended within 1 year for billing)
  useEffect(() => {
    if (!isOnlyOwner && contractId === 0 && leaseContractsList.length > 0) {
      // First try to find an active contract
      const activeContract = leaseContractsList.find(({ leaseStatus }) =>
        [
          CONTRACT_STATUS.contractStatusActivationReady,
          CONTRACT_STATUS.contractStatusTypeActive,
          CONTRACT_STATUS.contractStatusDepositPaid
        ].includes(leaseStatus)
      )

      if (activeContract) {
        setContractId(activeContract.id)
      } else if (hasOnlyEndedContracts && endedContracts.length > 0) {
        // If no active contract but user has ended contracts,
        // select the first ended contract. These users can only see billing category anyway.
        setContractId(endedContracts[0].id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseContractsList, hasOnlyEndedContracts, isOnlyOwner])

  useEffect(() => {
    if (formik.values.flatId === 0) {
      // First try to use leaseContractsFlats (for active contracts)
      if (!!leaseContractsFlats.length) {
        formik.setFieldValue('flatId', leaseContractsFlats[0].id)
      }
      // If leaseContractsFlats is empty (ended contracts), use userFlats filtered by tenant connection
      else if (userFlats && userFlats.length > 0) {
        const tenantFlats = userFlats.filter((flat) => flat.connectionType === 'tenant')
        if (tenantFlats.length > 0) {
          formik.setFieldValue('flatId', tenantFlats[0].id)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseContractsFlats, userFlats])

  const currentTerm = formik.values.availableDates[editingIndex]
  const firstEmptyTermIndex = [0, 1, 2].find(
    (k) => !formik.values.availableDates[k]?.availableDate
  )
  const hasAnyTerm = formik.values.availableDates.some((date) => date?.availableDate)
  // Filled slots plus a single "add next term" slot (only once at least one term exists).
  const visibleTermSlots = [0, 1, 2].filter(
    (k) =>
      !!formik.values.availableDates[k]?.availableDate ||
      (hasAnyTerm && k === firstEmptyTermIndex)
  )

  const handleRemoveTerm = (index: number) => {
    formik.setFieldValue(
      'availableDates',
      formik.values.availableDates.map((date, k) => (k === index ? undefined : date))
    )
    setEditingIndex(index)
  }

  return (
    <>
      <BackNavigationHeader
        onBack={handlePreviousStep}
        label={formatMessage({
          id: 'helpdesk.reportFaultTitle',
          defaultMessage: 'Zgłoś usterkę'
        })}
      />
      <BackNavigationContent>
        {isDesktop && (
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.r4r.navy, mb: '16px' }}>
            {formatMessage({
              id: 'helpdesk.reportFaultTitle',
              defaultMessage: 'Zgłoś usterkę'
            })}
          </Typography>
        )}
        <LinearProgress
          variant='determinate'
          value={formik.values.progress}
          sx={{
            height: '6px',
            borderRadius: 0,
            backgroundColor: theme.r4r.greyLight,
            '.MuiLinearProgress-bar': {
              backgroundColor: theme.r4r.navy
            }
          }}
        />
        {formik.values.step === 0 && (
          <>
            <Box
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: {
                  paddingBottom: '96px'
                }
              })}>
              {!!title && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.4px',
                    color: theme.r4r.azure,
                    textTransform: 'uppercase',
                    mt: '20px'
                  }}>
                  {title}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: theme.r4r.navy,
                  mt: !!title ? '4px' : '20px',
                  mb: '16px'
                }}>
                {formatMessage({
                  id: 'defectsReportFault.selectCategory',
                  defaultMessage: 'Wybierz kategorię'
                })}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
                  gap: '12px'
                }}>
                {categories.map(({ id, name, nameLocal, iconName }) => {
                  const isActive = parentCategoryId === id
                  const tint = getFaultTint(id)
                  return (
                    <ButtonBase
                      key={id}
                      onClick={() => {
                        const topic = language === 'pl' ? nameLocal : name
                        setParentCategoryId(id)
                        formik.setFieldValue('topics', [...formik.values.topics, topic])
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '16px 8px',
                        backgroundColor: isActive ? '#F0F1FF' : '#fff',
                        border: `2px solid ${isActive ? theme.r4r.navy : 'transparent'}`,
                        boxShadow: theme.r4r.cardShadow
                      }}>
                      <Box
                        sx={{
                          width: '42px',
                          height: '42px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: alpha(tint, 0.15)
                        }}>
                        <Icon name={iconName} sx={{ color: tint, fontSize: '22px' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: theme.r4r.ink,
                          textAlign: 'center',
                          lineHeight: 'normal'
                        }}>
                        {language === 'pl' ? nameLocal : name}
                      </Typography>
                    </ButtonBase>
                  )
                })}
              </Box>
            </Box>
            <MobileBottomFooter>
              {!!categoriesList.filter((category) =>
                !!parentCategoryId
                  ? category.parentCategoryId === parentCategoryId
                  : !!category.parentCategoryId
              ).length ? (
                <Button
                  data-cy='button-choose-category'
                  onClick={handleNextStep}
                  fullWidth
                  endIcon={<ArrowForward />}
                  sx={primaryButtonSx}
                  disabled={!parentCategoryId}>
                  {formatMessage({
                    id: 'helpdesk.next',
                    defaultMessage: 'Dalej'
                  })}
                </Button>
              ) : (
                <Button
                  data-cy='button-add-description'
                  onClick={() => {
                    formik.setFieldValue('step', 1)
                    formik.setFieldValue(
                      'progress',
                      topicsWithoutAvailabilityDate.includes(formik.values.categories[0]) ? 100 : 66
                    )
                    formik.setFieldValue('categories', [
                      ...formik.values.categories,
                      parentCategoryId
                    ])
                    formik.setFieldValue('topic', formik.values.topics.slice(-1)[0])
                  }}
                  fullWidth
                  endIcon={<ArrowForward />}
                  sx={primaryButtonSx}
                  disabled={!parentCategoryId}>
                  {formatMessage({
                    id: 'helpdesk.next',
                    defaultMessage: 'Dalej'
                  })}
                </Button>
              )}
            </MobileBottomFooter>
          </>
        )}
        {formik.values.step === 1 && (
          <>
            <Box
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: {
                  paddingBottom: '96px'
                }
              })}>
              <FieldLabel>
                {formatMessage({
                  id: 'defectsReportFault.topic',
                  defaultMessage: 'Temat'
                })}
              </FieldLabel>
              <TextField
                inputProps={{ 'data-cy': 'input-topic' }}
                name='topic'
                defaultValue={title}
                onChange={formik.handleChange}
                value={formik.values.topic}
                fullWidth
                disabled
                sx={{ '.MuiInputBase-root': { backgroundColor: '#fff' } }}
              />
              <FieldLabel>
                {formatMessage({
                  id: 'additionalServicesProtocolSpaces.form.comment',
                  defaultMessage: 'Opis'
                })}
              </FieldLabel>
              <TextField
                inputProps={{ 'data-cy': 'input-description' }}
                name='description'
                onChange={formik.handleChange}
                value={formik.values.description}
                placeholder={formatMessage({
                  id: 'helpdesk.descriptionPlaceholder',
                  defaultMessage: 'Opisz usterkę jak najdokładniej...'
                })}
                fullWidth
                rows={5}
                multiline
                sx={{ '.MuiInputBase-root': { backgroundColor: '#fff' } }}
              />

              {isOwnerUser && (
                <>
                  <FieldLabel>
                    {formatMessage({
                      id: 'defectsReportFault.flat',
                      defaultMessage: 'Mieszkanie'
                    })}
                  </FieldLabel>
                  <FormControl fullWidth>
                    <Select
                      data-cy='select-flat'
                      value={formik.values.flatId}
                      sx={{ backgroundColor: '#fff' }}
                      onChange={(event) =>
                        formik.setFieldValue('flatId', Number(event.target.value))
                      }>
                      {(userFlats || [])
                        .filter((flat) => flat.connectionType === 'owner')
                        .map(({ id, flatIdNumber }) => (
                          <MenuItem key={flatIdNumber} value={id}>
                            {flatIdNumber}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              )}
              {!isOnlyOwner && (
                <>
                  <FieldLabel>
                    {formatMessage({
                      id: 'defectsReportFault.contract',
                      defaultMessage: 'Umowa'
                    })}
                  </FieldLabel>
                  {withInactiveContracts ? (
                    <FormControl fullWidth>
                      <Select
                        data-cy='select-lease'
                        value={contractId}
                        sx={{ backgroundColor: '#fff' }}
                        onChange={(event) => setContractId(Number(event.target.value))}>
                        <ListSubheader>
                          {formatMessage({
                            id: 'defectsReportFault.activeContract',
                            defaultMessage: 'Aktywne umowy'
                          })}
                        </ListSubheader>
                        {leaseContractsList
                          .filter(({ leaseStatus }) =>
                            [
                              CONTRACT_STATUS.contractStatusActivationReady,
                              CONTRACT_STATUS.contractStatusTypeActive,
                              CONTRACT_STATUS.contractStatusDepositPaid
                            ].includes(leaseStatus)
                          )
                          .map(({ id, contractNumber }) => (
                            <MenuItem key={contractNumber} value={id}>
                              {contractNumber}
                            </MenuItem>
                          ))}

                        <Box sx={{ borderTop: '1px solid #CDCDCD' }} />
                        <ListSubheader>
                          {formatMessage({
                            id: 'defectsReportFault.inActiveContract',
                            defaultMessage: 'Nieaktywne umowy'
                          })}
                        </ListSubheader>
                        {endedContracts.map(({ id, contractNumber }) => (
                          <MenuItem key={contractNumber} value={id}>
                            {contractNumber}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl fullWidth>
                      <Select
                        data-cy='select-lease'
                        value={contractId}
                        sx={{ backgroundColor: '#fff' }}
                        onChange={(event) => setContractId(Number(event.target.value))}>
                        {leaseContractsList
                          .filter(({ leaseStatus }) =>
                            [
                              CONTRACT_STATUS.contractStatusActivationReady,
                              CONTRACT_STATUS.contractStatusTypeActive,
                              CONTRACT_STATUS.contractStatusDepositPaid
                            ].includes(leaseStatus)
                          )
                          .map(({ id, contractNumber }) => (
                            <MenuItem key={contractNumber} value={id}>
                              {contractNumber}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
              {isSelectedSurfaceFrozen && <OperationsFrozenMessage sx={{ mt: 4 }} />}
              <FieldLabel>
                {formatMessage({
                  id: 'helpdesk.attachments',
                  defaultMessage: 'Załączniki'
                })}
              </FieldLabel>
              {!!formik.values.attachments.length && (
                <Box mx={-1} mb={2}>
                  {formik.values.attachments.map((image, key) => (
                    <Badge
                      color='primary'
                      key={`${image.name}_${key}`}
                      sx={{
                        '.MuiBadge-badge': {
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%'
                        },
                        mt: 1,
                        mx: 1
                      }}
                      badgeContent={
                        <IconButton
                          size='small'
                          onClick={() => {
                            formik.setFieldValue(
                              'attachments',
                              formik.values.attachments.filter((_, k) => k !== key)
                            )
                          }}>
                          <Close fontSize='small' color='secondary' />
                        </IconButton>
                      }>
                      <Tooltip title={image.name} placement='top'>
                        <Box
                          sx={{
                            width: '96px',
                            height: '96px',
                            backgroundImage: `url("${URL.createObjectURL(image)}")`,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.400',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover'
                          }}
                        />
                      </Tooltip>
                    </Badge>
                  ))}
                </Box>
              )}
              {formik.values.attachments.length < 5 && <Dropzone onDrop={handleDrop} />}
              <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: 1 }}>
                {formatMessage({
                  id: 'common.file.multimedia',
                  defaultMessage: 'Pliki w formacie .jpg, .png, .jpeg, .webp, .pdf'
                })}
              </Typography>
            </Box>
            <MobileBottomFooter>
              {topicsWithoutAvailabilityDate.includes(formik.values.categories[0]) ? (
                <LoadingButton
                  data-cy='submit-without-date'
                  loading={formik.isSubmitting}
                  onClick={() => formik.submitForm()}
                  fullWidth
                  startIcon={<Send />}
                  sx={submitButtonSx}
                  disabled={!formik.values.flatId || isSelectedSurfaceFrozen}>
                  {formatMessage({
                    id: 'helpdesk.sendReport',
                    defaultMessage: 'Wyślij zgłoszenie'
                  })}
                </LoadingButton>
              ) : (
                <Button
                  data-cy='button-choose-date'
                  onClick={() => {
                    formik.setFieldValue('step', 2)
                    formik.setFieldValue('progress', 100)
                  }}
                  fullWidth
                  endIcon={<ArrowForward />}
                  sx={primaryButtonSx}
                  disabled={!formik.values.flatId || isSelectedSurfaceFrozen}>
                  {formatMessage({
                    id: 'helpdesk.next',
                    defaultMessage: 'Dalej'
                  })}
                </Button>
              )}
            </MobileBottomFooter>
          </>
        )}
        {formik.values.step === 2 && (
          <>
            <Box
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: {
                  paddingBottom: '118px'
                }
              })}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: theme.r4r.navy,
                  mt: '20px',
                  mb: '12px'
                }}>
                {formatMessage({
                  id: 'defectsReportFault.selectDate',
                  defaultMessage: 'Wybierz dogodny termin naprawy'
                })}
              </Typography>
              <Box sx={calendarCardSx}>
                <DateCalendar
                  data-cy={`input-date-${editingIndex}`}
                  disablePast
                  maxDate={calculateMaxDate(3)}
                  shouldDisableDate={disableDate}
                  value={currentTerm?.availableDate ? dayjs(currentTerm.availableDate) : null}
                  onChange={(value) => {
                    formik.setFieldValue(`availableDates[${editingIndex}].availableDate`, value)
                  }}
                  sx={calendarSx}
                />
              </Box>
              <FieldLabel>
                {formatMessage({
                  id: 'defectsReportFault.availableHourFrom',
                  defaultMessage: 'Od godziny'
                })}
              </FieldLabel>
              {renderTimeChips('from')}
              <FieldLabel>
                {formatMessage({
                  id: 'defectsReportFault.availableHourTo',
                  defaultMessage: 'Do godziny'
                })}
              </FieldLabel>
              {renderTimeChips('to')}
              <FieldLabel>
                {formatMessage({
                  id: 'helpdesk.selectedTerms',
                  defaultMessage: 'Wybrane terminy'
                })}
              </FieldLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {!hasAnyTerm && (
                  <Typography sx={{ fontSize: '12px', color: theme.r4r.grey }}>
                    {formatMessage({
                      id: 'helpdesk.noTermsSelected',
                      defaultMessage: 'Brak wybranych terminów'
                    })}
                  </Typography>
                )}
                {visibleTermSlots.map((i, order) => {
                  const term = formik.values.availableDates[i]
                  const isFilled = !!term?.availableDate
                  const isEditing = editingIndex === i
                  const separator =
                    order > 0 ? (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 700,
                          letterSpacing: '0.6px',
                          color: theme.r4r.grey,
                          textAlign: 'center',
                          my: '8px'
                        }}>
                        {formatMessage({
                          id: 'defectsReportFault.or',
                          defaultMessage: 'Lub'
                        })}
                      </Typography>
                    ) : null

                  if (!isFilled) {
                    return (
                      <Box key={i}>
                        {separator}
                        <ButtonBase
                          data-cy={`button-add-term-${i}`}
                          onClick={() => setEditingIndex(i)}
                          sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            padding: '14px',
                            backgroundColor: '#fff',
                            border: isEditing
                              ? `1px solid ${theme.r4r.navy}`
                              : `1px dashed ${theme.r4r.greyLight}`,
                            color: theme.r4r.grey,
                            fontSize: '13px',
                            fontFamily: theme.typography.fontFamily
                          }}>
                          {formatMessage(
                            {
                              id: 'helpdesk.addTerm',
                              defaultMessage: 'Dodaj termin {number}'
                            },
                            { number: i + 1 }
                          )}
                        </ButtonBase>
                      </Box>
                    )
                  }

                  const from = term?.availableHourFrom
                  const to = term?.availableHourTo
                  const termLabel = `${dayjs(term.availableDate).format('DD.MM.YYYY')}${
                    from ? ` · ${dayjs(from).format('HH:mm')}` : ''
                  }${to ? ` – ${dayjs(to).format('HH:mm')}` : ''}`

                  return (
                    <Box key={i}>
                      {separator}
                      <Box
                        onClick={() => setEditingIndex(i)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 14px',
                          backgroundColor: '#fff',
                          border: `1px solid ${isEditing ? theme.r4r.navy : theme.r4r.greyLight}`,
                          cursor: 'pointer'
                        }}>
                        <Box>
                          <Typography
                            sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.navy }}>
                            {termLabel}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
                            {formatMessage(
                              {
                                id: 'helpdesk.term',
                                defaultMessage: 'Termin {number}'
                              },
                              { number: i + 1 }
                            )}
                          </Typography>
                        </Box>
                        <IconButton
                          size='small'
                          onClick={(event) => {
                            event.stopPropagation()
                            handleRemoveTerm(i)
                          }}>
                          <HighlightOff sx={{ color: theme.r4r.red }} />
                        </IconButton>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
            <MobileBottomFooter>
              {formik.values.availableDates.some((date) => date?.availableDate) ? (
                <LoadingButton
                  data-cy='submit-with-date'
                  loading={formik.isSubmitting}
                  onClick={() => formik.submitForm()}
                  fullWidth
                  startIcon={<Send />}
                  sx={submitButtonSx}
                  disabled={!formik.values.flatId || isSelectedSurfaceFrozen}>
                  {formatMessage({
                    id: 'helpdesk.sendReport',
                    defaultMessage: 'Wyślij zgłoszenie'
                  })}
                </LoadingButton>
              ) : (
                <LoadingButton
                  data-cy='button-skip-and-submit'
                  onClick={() => formik.submitForm()}
                  loading={formik.isSubmitting}
                  fullWidth
                  disabled={!formik.values.flatId || isSelectedSurfaceFrozen}
                  sx={{
                    color: theme.r4r.navy,
                    fontSize: '13px',
                    fontWeight: 700,
                    letterSpacing: '0.6px',
                    borderRadius: 0,
                    padding: '12px 20px'
                  }}>
                  {formatMessage({
                    id: 'defectsReportFault.skipAndSubmit',
                    defaultMessage: 'Pomiń i zgłoś'
                  })}
                </LoadingButton>
              )}
            </MobileBottomFooter>
          </>
        )}
        {formik.values.step === 3 && (
          <>
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                gap: '12px',
                py: '64px',
                [theme.breakpoints.down('md')]: {
                  minHeight: '70vh',
                  py: 0
                }
              })}>
              <Box
                sx={{
                  width: '72px',
                  height: '72px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.r4r.green, 0.12)
                }}>
                <CheckCircleOutline sx={{ color: theme.r4r.green, fontSize: '40px' }} />
              </Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.r4r.navy }}>
                {formatMessage({
                  id: 'defectsReportFault.success',
                  defaultMessage: 'Usterka została zgłoszona'
                })}
              </Typography>
            </Box>
            <MobileBottomFooter>
              <Button
                data-cy='button-back'
                onClick={() => navigate('/faults')}
                fullWidth
                sx={primaryButtonSx}>
                {formatMessage({
                  id: 'defectsReportFault.back',
                  defaultMessage: 'Wróć do strony startowej'
                })}
              </Button>
            </MobileBottomFooter>
          </>
        )}
      </BackNavigationContent>
    </>
  )
}

export default DefectsReportFault
